import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Switch, Tabs, Typography } from "antd"
import Input from "components/form/Input"
import PhotoSelector from "components/form/PhotoSelector"
import TagsInput from "components/form/TagsInput"
import RichTextEditor, { toEditorHtml } from "components/common/RichTextEditor"
import DOMPurify from "dompurify"
import Icon from "components/common/Icon"
import Post from "./Post"
import TextGeneratorWindow from "components/common/TextGeneratorWindow"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useForm, { cleanNestedAttributes, useNestedFields } from "hooks/useForm"
import { findScrollParent } from "helpers/DOM"
import { useTranslation } from "react-i18next"
import { serialize } from "object-to-formdata"
import { useDispatch, useSelector } from "react-redux"
import { savePost } from "store/posts"
import { metadataSelector, modelSelector } from "store/selectors"
import { errorsShownSelector } from "modules/errors/selectors"
import { colors } from "themes/variables"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import styles from "./index.module.css"
import { Icon as UIIcon } from "ui/Icon"
const Form = ({ closeHandler, onChanged }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const tabsRef = useRef(null)
  const [activeSection, setActiveSection] = useState("form")
  const [profileDescription, setProfileDescription] = useState("")
  const [isShowAiModal, setisShowAiModal] = useState(false)
  const post = useSelector(modelSelector("post"))
  const metadata = useSelector(metadataSelector("posts"))
  const hasErrorFields = useSelector(errorsShownSelector)
  const { trip } = post
  const isPersisted = !!post.id
  const [form, changeHandler, submitHandler, submitCallback, , isChanged] = useForm({ ...post, trip_id: trip?.id || post.trip_id }, [
    "title",
    "content",
    "photos",
    "tags",
    "trip_id",
    "send_trip_photos",
    "facebook_share",
    "instagram_share",
    "twitter_share"
  ])
  const [, , addTagHandler, removeTagHandler, , updateTagHandler] = useNestedFields(form, "tags", ["title"], changeHandler)

  const [contentError, setContentError] = useState("")
  const normalizedForId = useRef(null)

  const setContent = (value) => changeHandler({ target: { name: "content", value } })
  // The editor stores HTML, so measure the visible text length for the AI/
  // validation gating instead of counting markup characters.
  const contentTextLength = (form.content || "").replace(/<[^>]*>/g, "").trim().length

  const changeTab = (tab) => () => {
    const scrollWrapper = findScrollParent(tabsRef.current)
    if (scrollWrapper) scrollWrapper.scrollTo({ left: 0, top: 0, behavior: "smooth" })
    setActiveSection(tab)
  }

  const textGeneratorHandler = ({ target }) => {
    const tags = target.value.match(/#(\w|_)+/gm, "") || []
    const value = target.value.replace(/#(\w|_)+/gm, "").trim()

    changeHandler({ target: { name: target.name, value } })
    tags.forEach((tag) => addTagHandler(null, { title: tag }))
  }

  submitCallback(() => {
    if (contentTextLength < 25) {
      setContentError(t("pro.ai_enhancement.description_too_short"));
      changeTab("form")();
      return;
    } else {
      setContentError("");
    }
    const properties_attributes = cleanNestedAttributes(form.photos, ["file", "file_type"])
    const tags_attributes = cleanNestedAttributes(form.tags, ["title"])
    const postAttributes = { ...form, properties_attributes, tags_attributes, content: DOMPurify.sanitize(form.content || "") }

    delete postAttributes.photos
    delete postAttributes.tags

    const formData = serialize({ post: postAttributes }, { indices: true })
    dispatch(savePost(post.id, formData)).then(closeHandler)
  })

  useEffect(() => {
    if (typeof onChanged === "function") onChanged(isChanged)
  }, [isChanged, onChanged])

  useEffect(() => {
    if (hasErrorFields) changeTab("form")()
  }, [hasErrorFields])

  // Convert legacy plain-text content (with line breaks) to HTML once per
  // loaded record so it renders correctly in the rich text editor.
  useEffect(() => {
    if (normalizedForId.current === (post.id ?? "new")) return
    normalizedForId.current = post.id ?? "new"
    const html = toEditorHtml(form.content || "")
    if (html !== (form.content || "")) setContent(html)
  }, [post.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FirstLoading name="posts.post" new>
      <form onSubmit={submitHandler} ref={tabsRef}>
        <Tabs
          activeKey={activeSection}
          renderTabBar={() => null}
          items={[
            {
              key: "form",
              children: (
                <Flex vertical gap={20}>
                  <Flex align="center" justify="space-between" className={`${styles.card} ${styles.stickyHeader}`}>
                    <Typography.Title level={3} className={styles.title}>
                      {isPersisted ? t("posts.edit") : t("posts.add_new")}
                    </Typography.Title>
                    <Flex gap={10} className={styles.headerActions} justify="flex-end">
                      <Button onClick={closeHandler}>{t("global.cancel")}</Button>
                      <Button type="primary" onClick={changeTab("preview")} disabled={!isChanged}>
                        {t("global.preview")}
                      </Button>
                    </Flex>
                  </Flex>

                  <Flex vertical gap={30} className={`${styles.card} ${styles.cardScroll}`}>
                    <div>
                      <label htmlFor="title" className={styles.label}>
                        {t("post.labels.title")}*
                      </label>
                      <Input
                        id="title"
                        type="text"
                        name="title"
                        placeholder={t("post.labels.title")}
                        value={form.title || ""}
                        onChange={changeHandler}
                        withError
                      />
                    </div>
                    <div>
                      <Flex align="center" justify="space-between" gap={10} style={{ marginBottom: 8 }}>
                        <label htmlFor="content" className={styles.label}>
                          {t("post.labels.content")}*
                        </label>
                        <Button
                          type="primary"
                          icon={<UIIcon name="aiEnhancement" color={contentTextLength < 25 ? colors.textPrimary : colors.disabled} />}
                          disabled={contentTextLength < 25}
                          onClick={() => setisShowAiModal(true)}
                        >
                          {t("pro.ai_enhancement.button_text")}
                        </Button>
                      </Flex>
                      <RichTextEditor
                        id="content"
                        name="content"
                        placeholder={t("post.placeholders.content")}
                        value={form.content}
                        onChange={setContent}
                      />
                      {contentError && <div className={styles.errorMessage}>{contentError}</div>}
                    </div>
                    <div>
                      <label className={styles.label}>{t("post.labels.hashtags")}</label>
                      <TagsInput
                        name="tags"
                        type="textarea"
                        placeholder={t("post.placeholders.hashtags")}
                        tags={form.tags}
                        addTagHandler={addTagHandler}
                        removeTagHandler={removeTagHandler}
                        updateTagHandler={updateTagHandler}
                      />
                    </div>
                    <div>
                      <label className={styles.label}>{t("post.labels.photos", { max: 4 })}</label>
                      <PhotoSelector imageHeight={104} form={form} changeHandler={changeHandler} max={4} small start label={false} />
                    </div>
                  </Flex>
                  <Flex vertical gap={30} className={styles.card}>
                    <div>
                      <label htmlFor="trip_id" className={styles.label}>
                        {t("post.labels.trip")}
                      </label>
                      <Input
                        id="trip_id"
                        type="select"
                        name="trip_id"
                        placeholder={t("post.placeholders.trip")}
                        value={form.trip_id || ""}
                        onChange={changeHandler}
                        withError
                        disabled={!metadata.trips_collection?.length}
                      >
                        <option value="">{t("post.placeholders.trip")}</option>
                        {metadata.trips_collection?.map(({ id, title }) => (
                          <option key={id} value={id}>
                            {title}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <Flex vertical gap={12} className={styles.toggleRow}>
                      <label className={styles.label}>{t("user.email")}</label>
                      <Flex align="center" justify="space-between">
                        <span className={styles.toggleText}>{t(`post.labels.send_photos`)}</span>
                        <Switch
                          checked={form.send_trip_photos}
                          onChange={(checked) => changeHandler({ target: { name: "send_trip_photos", type: "checkbox", checked } })}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                  <div className={styles.card}>
                    <Flex vertical gap={12} className={styles.toggleRow}>
                      <Flex align="center" justify="space-between">
                        <label className={styles.label}>{t("post.labels.social")}</label>
                        <span className={styles.comingSoon}>{t("campaign.coming_soon")}</span>
                      </Flex>
                      {["facebook", "instagram", "twitter"].map((social) => (
                        <Flex key={social} align="center" justify="space-between">
                          <span className={styles.toggleText}>{t(`global.social.${social}`)}</span>
                          <Switch
                            disabled
                            checked={form[`${social}_share`]}
                            onChange={(checked) => changeHandler({ target: { name: `${social}_share`, type: "checkbox", checked } })}
                          />
                        </Flex>
                      ))}
                    </Flex>
                  </div>
                </Flex>
              )
            },
            {
              key: "preview",
              children: (
                <Flex vertical gap={20}>
                  <div className={`${styles.card} ${styles.stickyHeader}`}>
                    <Flex align="center" justify="space-between">
                      <Typography.Title level={3} className={styles.title}>
                        {isPersisted ? t("posts.edit") : t("posts.add_new")}
                      </Typography.Title>
                      <Flex gap={10} align="center">
                        <Button type="text" onClick={changeTab("form")}>
                          <Icon iconName="ArrowLeft" width={18} height={14} block />
                        </Button>
                        <Button type="primary" htmlType="submit" disabled={!isChanged}>
                          {isPersisted ? t("global.save") : t("global.post")}
                        </Button>
                      </Flex>
                    </Flex>
                  </div>
                  <Post post={form} edit />
                </Flex>
              )
            }
          ]}
        />
      </form>
      <TextGeneratorWindow name="content" containerId="marketing" onSubmit={textGeneratorHandler} />
      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          onCancel={() => {
            setisShowAiModal(false)
            setProfileDescription('')
          }}
          profileDescription={profileDescription}
          description={form.content || ""}
          setDescription={setContent}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setisShowAiModal(false)
          }}
          text={t("pro.ai_enhancement.form.marketing_textarea_label")}
          modalTitle={t("pro.ai_enhancement.marketing_modal_title")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("pro.ai_enhancement.form.post_ai_info")}
        />
      )}
    </FirstLoading>
  )
}

Form.propTypes = {
  closeHandler: PropTypes.func.isRequired,
  onChanged: PropTypes.func
}

export default Form
